import React, { useState, useEffect } from 'react';
import { apiPost } from '../utils/api';
import styles from './CreateEpisodeModal.module.css';

const CreateEpisodeModal = ({ isOpen, onClose, podcastId, defaultHost, onEpisodeCreated, skipForm = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdEpisodeId, setCreatedEpisodeId] = useState(null);

  // Auto-start episode creation when skipForm is true
  useEffect(() => {
    if (isOpen && skipForm) {
             // Set default form data and start creation
             setFormData({
               title: 'Episode 1',
               notes: ''
             });
      // Start the creation process immediately
      setTimeout(() => {
        startEpisodeCreation();
      }, 100);
    }
  }, [isOpen, skipForm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const startEpisodeCreation = async () => {
    setLoading(true);
    setError('');

    try {
      // Create episode
      const episodeResponse = await apiPost(`/podcast/${podcastId}/episodes/create-episode`, {
        title: formData.title,
        notes: formData.notes
      });

      if (!episodeResponse.ok) {
        throw new Error('Failed to create episode');
      }

      const episodeData = await episodeResponse.json();
      setCreatedEpisodeId(episodeData.id);

      // Start script generation in background
      try {
        await apiPost(`/podcast/episode/${episodeData.id}/generate-script`);
      } catch (scriptErr) {
        console.warn('Script generation failed to start:', scriptErr);
        // Don't fail the whole process if script generation doesn't start
      }

      // Show success message
      setShowSuccess(true);

      // Auto-close after 3 seconds
      setTimeout(() => {
        onClose();
        if (onEpisodeCreated) {
          onEpisodeCreated();
        }
        // Reset form for next time
        setFormData({
          title: '',
          notes: ''
        });
        setShowSuccess(false);
      }, 3000);

    } catch (err) {
      setError('Failed to create episode. Please try again.');
      console.error('Error creating episode:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await startEpisodeCreation();
  };

  const handleCancel = () => {
    setFormData({
      title: '',
      notes: ''
    });
    setError('');
    setShowSuccess(false);
    onClose();
  };


  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Create New Episode</h2>
          {!showSuccess && (
            <button 
              className={styles.closeButton}
              onClick={handleCancel}
              type="button"
            >
              ×
            </button>
          )}
        </div>

        {!showSuccess ? (
          <form onSubmit={handleSubmit} className={styles.modalForm}>
            <div className={styles.formGroup}>
              <label htmlFor="title" className={styles.label}>
                Episode Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={styles.input}
                placeholder="Enter episode title"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="notes" className={styles.label}>
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className={styles.textarea}
                placeholder="Enter episode notes (optional)"
                rows="3"
              />
            </div>


            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !formData.title.trim()}
              >
                {loading ? 'Creating...' : 'Create Episode'}
              </button>
            </div>
          </form>
        ) : (
          <div className={styles.successContainer}>
            <div className={styles.successIcon}>
              <div className={styles.checkmark}>✓</div>
            </div>
            <div className={styles.successContent}>
              <h3>Episode Created Successfully!</h3>
              <p>Your episode has been created and will be ready shortly. The script and audio are being processed in the background.</p>
              <div className={styles.processingNote}>
                <p>⏳ Processing will continue in the background...</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateEpisodeModal;
